import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../styles/PriceLineChart.css';
import { formatterCompact } from '../styles/GlobalStyles';
import { getPriceField } from '../functions';

const SVG_WIDTH = 280;
const SVG_HEIGHT = 60;
const PAD_X = 16;
const PAD_TOP = 20;
const PAD_BOTTOM = 4;
// Flat-mode dimensions — dots all sit on the chart's mid-line, so the
// chart can collapse to a much shorter strip without losing legibility.
// Padding leaves just enough room around DOT_R=5 for the hover-grow.
const SVG_HEIGHT_FLAT = 14;
const PAD_VERT_FLAT = 5;
const DOT_R = 5;
const DOT_R_HOVER = 6.5;

// Map a comp's listingDetails.listingStatus to the same three-bucket key the
// map bubbles use (see CompMapBubble.js): for_sale → forSale, pending →
// pending, everything else → sold. Keeps the line-chart dots colour-coded the
// same way as the map markers so the two views read as one legend.
function getListingStatusKey(comp) {
    const status = comp?.listingDetails?.listingStatus;
    if (status === 'for_sale') return 'forSale';
    if (status === 'pending') return 'pending';
    return 'sold';
}

function PriceLineChart({ comps, property, clickedZPID, onDotClick, isDetailView, compScores, flat, tooltipBelow, ceilingZpid, floorZpid, hideScores }) {
    const [hoveredZpid, setHoveredZpid] = useState(null);
    const wrapperRef = useRef(null);
    const tooltipRef = useRef(null);
    // Pixel shift applied to the tooltip body to keep it inside the chart's
    // bounds. The arrow gets the inverse shift so it still points at the dot.
    const [tooltipShift, setTooltipShift] = useState(0);
    const svgH = flat ? SVG_HEIGHT_FLAT : SVG_HEIGHT;
    const padTop = flat ? PAD_VERT_FLAT : PAD_TOP;
    const padBot = flat ? PAD_VERT_FLAT : PAD_BOTTOM;

    const getScore = (comp) => {
        if (!compScores) return null;
        const zpid = comp?.uniqueIds?.zillowId;
        if (!zpid) return null;
        const s = compScores[String(zpid)];
        return typeof s === 'number' && Number.isFinite(s) ? s : null;
    };

    const { minValue, maxValue, points, minScore, maxScore } = useMemo(() => {
        if (!comps || comps.length === 0) return { minValue: 0, maxValue: 0, points: [], minScore: null, maxScore: null };

        const withPrice = comps
            .map(comp => ({ comp, price: getPriceField(comp) }))
            .filter(({ price }) => typeof price === 'number' && !isNaN(price))
            .sort((a, b) => a.price - b.price);

        if (withPrice.length === 0) return { minValue: 0, maxValue: 0, points: [], minScore: null, maxScore: null };

        const min = withPrice[0].price;
        const max = withPrice[withPrice.length - 1].price;
        const priceRange = max - min;
        const plotW = SVG_WIDTH - PAD_X * 2;
        const plotH = svgH - padTop - padBot;

        const pts = withPrice.map(({ comp, price }, i) => {
            // x: evenly spaced across the chart so dots don't overlap
            const x = withPrice.length === 1
                ? PAD_X + plotW / 2
                : PAD_X + (i / (withPrice.length - 1)) * plotW;
            // y: price mapped to height (higher price = higher on chart),
            // unless flat mode is on, OR all comps are at the same price
            // (single-comp group, or every comp priced identically) — in
            // those no-spread cases the dot(s) sit at the chart's mid-line
            // so the chart reads as a 1D legend without crashing into the
            // axis floor.
            const noSpread = priceRange === 0;
            const yNorm = (flat || noSpread) ? 0.5 : (price - min) / priceRange;
            const y = svgH - padBot - yNorm * plotH;
            return { comp, price, x, y };
        });

        return {
            minValue: min,
            maxValue: max,
            points: pts,
            minScore: getScore(withPrice[0].comp),
            maxScore: getScore(withPrice[withPrice.length - 1].comp)
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comps, compScores, flat, svgH, padTop, padBot]);

    // Measure tooltip + wrapper after each hover to compute exact pixel shift
    // that keeps the tooltip body within the chart's bounds. The arrow then
    // gets the inverse shift so it still points at the hovered dot. Runs in
    // useLayoutEffect so the corrected position commits before paint — no
    // visible jump on hover. Resets to 0 when no dot is hovered so the next
    // hover doesn't briefly inherit a stale shift. Declared above the
    // early-return branches below so the hook order stays stable.
    useLayoutEffect(() => {
        if (!hoveredZpid) {
            if (tooltipShift !== 0) setTooltipShift(0);
            return;
        }
        if (!wrapperRef.current || !tooltipRef.current) return;
        const pt = points.find(p => p.comp.uniqueIds?.zillowId === hoveredZpid);
        if (!pt) return;
        const wrapperW = wrapperRef.current.offsetWidth;
        const tipW = tooltipRef.current.offsetWidth;
        const dotX = (pt.x / SVG_WIDTH) * wrapperW;
        const halfW = tipW / 2;
        const PAD = 4;
        let shift = 0;
        if (dotX - halfW < PAD) shift = PAD + halfW - dotX;
        else if (dotX + halfW > wrapperW - PAD) shift = wrapperW - PAD - halfW - dotX;
        if (shift !== tooltipShift) setTooltipShift(shift);
    }, [hoveredZpid, points, tooltipShift]);

    if (!comps || comps.length === 0) return null;
    if (points.length === 0) return null;

    // Build smooth cubic bezier path through points
    const buildPath = () => {
        if (points.length === 0) return '';
        if (points.length === 1) return '';
        if (points.length === 2) {
            return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
        }

        let d = `M ${points[0].x} ${points[0].y}`;
        for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[Math.max(0, i - 1)];
            const p1 = points[i];
            const p2 = points[i + 1];
            const p3 = points[Math.min(points.length - 1, i + 2)];

            // Catmull-Rom to cubic bezier control points
            const tension = 0.3;
            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }
        return d;
    };

    const pathD = buildPath();

    // Clickability is driven by `onDotClick` presence — anywhere that wants
    // dot-click action must pass the callback. `isDetailView` no longer gates
    // clicks (it only controls the address line in the tooltip).
    const handleDotClick = (comp, e) => {
        if (!onDotClick) return;
        const zpid = comp.uniqueIds?.zillowId;
        if (!zpid) return;
        // Stop propagation so dots inside a clickable wrapper (e.g. ArvWorkspace's
        // comp-group tiles, which wrap the chart in a <button>) don't double-fire
        // the wrapper's onClick.
        if (e) e.stopPropagation();
        onDotClick(comp);
    };

    return (
        <div className="plc-container">
            <div className="plc-svg-wrap" ref={wrapperRef}>
            <svg
                className="plc-svg"
                viewBox={`0 0 ${SVG_WIDTH} ${svgH}`}
                preserveAspectRatio="xMidYMid meet"
            >
                {/* Curved line */}
                {pathD && (
                    <path
                        d={pathD}
                        className="plc-line"
                        fill="none"
                    />
                )}

                {/* Dots */}
                {points.map(({ comp, price, x, y }, index) => {
                    const zpid = comp.uniqueIds?.zillowId;
                    const isSelected = clickedZPID && zpid && String(clickedZPID) === String(zpid);
                    const isHovered = hoveredZpid === zpid;
                    const isCeiling = ceilingZpid != null && zpid != null && String(ceilingZpid) === String(zpid);
                    const isFloor = floorZpid != null && zpid != null && String(floorZpid) === String(zpid);
                    // Role (ceiling/floor) hue beats status hue — only apply
                    // the listing-status class when the dot isn't already a
                    // role-coloured one.
                    const statusKey = (!isCeiling && !isFloor) ? getListingStatusKey(comp) : null;
                    const r = isHovered ? DOT_R_HOVER : DOT_R;

                    return (
                        <g key={zpid || index}>
                            {/* Larger invisible hit area */}
                            <circle
                                cx={x}
                                cy={y}
                                r={14}
                                fill="transparent"
                                className={onDotClick ? 'plc-hit-area-clickable' : ''}
                                onMouseEnter={() => setHoveredZpid(zpid)}
                                onMouseLeave={() => setHoveredZpid(null)}
                                onClick={(e) => handleDotClick(comp, e)}
                            />
                            {/* Outer ring. Hue carries role (ceiling=red, floor=jade,
                                else gray); state (clicked) is conveyed by the
                                -selected variant which only changes the stroke,
                                so the role color isn't lost when a role-dot is
                                clicked. */}
                            <circle
                                cx={x}
                                cy={y}
                                r={r}
                                className={`plc-dot-outer ${statusKey ? `plc-dot-outer-${statusKey}` : ''} ${isCeiling ? 'plc-dot-outer-ceiling' : ''} ${isFloor ? 'plc-dot-outer-floor' : ''} ${isSelected ? 'plc-dot-outer-selected' : ''} ${isHovered ? 'plc-dot-outer-hovered' : ''}`}
                            />
                            {/* Inner dot */}
                            <circle
                                cx={x}
                                cy={y}
                                r={r * 0.6}
                                className={`plc-dot-inner ${statusKey ? `plc-dot-inner-${statusKey}` : ''} ${isCeiling ? 'plc-dot-inner-ceiling' : ''} ${isFloor ? 'plc-dot-inner-floor' : ''}`}
                            />
                        </g>
                    );
                })}
            </svg>

            {/* Tooltip rendered outside SVG for better styling */}
            {hoveredZpid && (() => {
                const pt = points.find(p => p.comp.uniqueIds?.zillowId === hoveredZpid);
                if (!pt) return null;
                const leftPct = (pt.x / SVG_WIDTH) * 100;
                const topPct = (pt.y / svgH) * 100;
                return (
                    <div
                        ref={tooltipRef}
                        className={"plc-tooltip" + (tooltipBelow ? " plc-tooltip-below" : "")}
                        style={{
                            '--tt-left': `${leftPct}%`,
                            '--tt-top': `${topPct}%`,
                            '--tt-shift': `${tooltipShift}px`,
                        }}
                    >
                        <span className="label-bold">{formatterCompact.format(pt.price)}</span>
                        {!hideScores && (() => {
                            const s = getScore(pt.comp);
                            return s != null ? (
                                <span className="plc-tooltip-address xs-label">score {s}</span>
                            ) : null;
                        })()}
                        <span className="plc-tooltip-address xs-label">
                            {pt.comp.addressDetails?.streetAddress || 'Unknown'}
                        </span>
                    </div>
                );
            })()}
            </div>

            {/* Price labels — one centered label when there's no spread
                (single comp, or all comps priced identically), otherwise
                min on the left and max on the right. */}
            {minValue === maxValue ? (
                <div className="plc-labels plc-labels-centered">
                    <div className="plc-label-stack">
                        <span className="xs-label colour-secondary">{formatterCompact.format(minValue)}</span>
                        {!hideScores && minScore != null && (
                            <span className="xs-label colour-tertiary">score {minScore}</span>
                        )}
                    </div>
                </div>
            ) : (
                <div className="plc-labels">
                    <div className="plc-label-stack">
                        <span className="xs-label colour-secondary">{formatterCompact.format(minValue)}</span>
                        {!hideScores && minScore != null && (
                            <span className="xs-label colour-tertiary">score {minScore}</span>
                        )}
                    </div>
                    <div className="plc-label-stack">
                        <span className="xs-label colour-secondary">{formatterCompact.format(maxValue)}</span>
                        {!hideScores && maxScore != null && (
                            <span className="xs-label colour-tertiary">score {maxScore}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default PriceLineChart;
